// MyEvents.jsx
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Lock,
  ArrowRight,
  Calendar,
  Globe2,
  Ticket,
  CheckCircle2,
  AlertCircle,
  EyeOff,
  Eye,
} from "lucide-react";
import { toast } from "sonner";
import "./MyEvents.css";

import { getMyEvents, getEventById } from "@/Services/EventServices";

const BACKEND = process.env.REACT_APP_BACKEND_URL || window.location.origin;

/** "(GMT-4)" -> "-04:00", "(GMT+5:30)" -> "+05:30" */
function gmtLabelToOffset(tz) {
  const s = String(tz || "")
    .replace(/[()]/g, "")
    .trim(); // "GMT-4"
  const m = s.match(/GMT\s*([+-])\s*(\d{1,2})(?::(\d{2}))?/i);
  if (!m) return "Z";
  const sign = m[1] === "-" ? "-" : "+";
  const hh = String(m[2]).padStart(2, "0");
  const mm = String(m[3] || "00").padStart(2, "0");
  return `${sign}${hh}:${mm}`;
}

function isoFromParts(dateStr, timeStr, tzLabel) {
  if (!dateStr || !timeStr) return "";
  const offset = gmtLabelToOffset(tzLabel);
  // dateStr: "2026-06-15", timeStr: "10:00"
  // -> "2026-06-15T10:00:00-04:00"
  return `${dateStr}T${timeStr}:00${offset === "Z" ? "Z" : offset}`;
}

function normalizeBookings(apiData) {
  const bookings = apiData?.bookings;
  if (!Array.isArray(bookings)) return [];

  return bookings.map((b) => {
    const ev = b.eventDetails;

    const eventId = ev?._id || b.newsId || b.eventId || b.event_id || "";
    const title = ev?.eventName || "Event";

    const tz = ev?.timezone || "(GMT+0)";
    const startIso = ev ? isoFromParts(ev.startDate, ev.startTime, tz) : "";
    const endIso = ev
      ? isoFromParts(ev.endDate || ev.startDate, ev.endTime, tz)
      : "";

    const price = Number(ev?.ticketPrice ?? 0);
    const currency = ev?.currency || "USD";

    // You don't have payment fields in this response, so we infer:
    const payment_status = price <= 0 ? "free" : "pending";

    return {
      registration_id: b._id,
      payment_status,
      price_paid: price, // amount due/paid unknown; using ticketPrice for badge
      currency,
      ref_code: b.ticketNumber || "",
      sponsor_attribution:
        (Array.isArray(ev?.sponcers) && ev.sponcers[0]?.title) || "",
      top_table_ids: [],

      // what your card expects
      event: {
        id: eventId,
        title,
        start_time: startIso,
        end_time: endIso,
        timezone: tz,

        // extra display helpers to avoid Intl timeZone with "(GMT-4)"
        displayDate: ev?.startDateFormatted || ev?.startDate || "",
        displayTimeRange:
          ev?.startTime && ev?.endTime ? `${ev.startTime} – ${ev.endTime}` : "",
      },
    };
  });
}

export default function MyEvents() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchMine = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMyEvents(); // <-- /mybookings
      if (!res?.success) {
        toast.error("Failed to load your events.");
        setRows([]);
        return;
      }

      let normalized = normalizeBookings(res);

      // OPTIONAL: if some bookings have eventDetails=null, try fetching the event by id (newsId)
      const missing = res.bookings.filter((b) => !b.eventDetails && b.newsId);
      if (missing.length > 0) {
        const fetched = await Promise.all(
          missing.map(async (b) => {
            try {
              const evRes = await getEventById(b.newsId);
              // adjust based on your /get_event/:id response shape
              const ev =
                evRes?.eventDetails || evRes?.data || evRes?.event || evRes;

              return {
                ...b,
                eventDetails: {
                  _id: ev?._id || b.newsId,
                  eventName: ev?.eventName || ev?.title || "Event",
                  startDate: ev?.startDate,
                  endDate: ev?.endDate,
                  startTime: ev?.startTime,
                  endTime: ev?.endTime,
                  timezone: ev?.timezone || "(GMT+0)",
                  ticketPrice: ev?.ticketPrice ?? ev?.price ?? 0,
                  currency: ev?.currency || "USD",
                  startDateFormatted: ev?.startDateFormatted,
                },
              };
            } catch {
              return b;
            }
          }),
        );

        // rebuild with patched bookings
        const patched = res.bookings.map((b) => {
          const hit = fetched.find((x) => x._id === b._id);
          return hit || b;
        });

        normalized = normalizeBookings({ bookings: patched });
      }

      setRows(normalized);
    } catch (e) {
      toast.error("Failed to load your events.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && typeof user === "object") fetchMine();
  }, [user, fetchMine]);

  if (user === null) return null;

  if (user === false) {
    return (
      <div className="myEventsLockedPage" data-testid="my-events-locked">
        <section className="container-x myEventsLockedSection">
          <Lock size={20} className="myEventsLockIcon" />
          <p className="overline myEventsLockedOverline">My events</p>
          <h1 className="myEventsLockedHeading">
            Sign in to access
            <br />
            <span className="myEventsLockedHeadingAccent">
              the events you've claimed.
            </span>
          </h1>
          <p className="myEventsLockedBody">
            Once you've registered for an event, signing in here gives you the
            agenda, your seat preferences, and the join link as soon as it's
            live.
          </p>
          <div className="myEventsLockedActions">
            <Link to="/login" className="btn-gold">
              Sign in <ArrowRight size={16} />
            </Link>
            <Link to="/register" className="btn-ghost">
              Create a profile
            </Link>
          </div>
        </section>
      </div>
    );
  }

  if (rows === null || loading) {
    return (
      <div
        className="container-x myEventsLoadingState"
        data-testid="my-events-loading"
      >
        Loading your events…
      </div>
    );
  }

  return (
    <div data-testid="my-events-page" className="myEventsPage">
      <section
        className="myEventsHero"
        style={{
          background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)",
        }}
      >
        <div className="container-x myEventsHeroInner">
          <p className="overline myEventsHeroOverline">My events</p>
          <h1 className="myEventsHeroHeading">
            Hi, {user.name?.split(" ")[0] || "there"}.
          </h1>
          <p className="myEventsHeroBody">
            {rows.length === 0
              ? "You haven't claimed a seat yet."
              : `You're registered for ${rows.length} event${rows.length === 1 ? "" : "s"}.`}
          </p>

          {rows.length === 0 && (
            <Link
              to="/events"
              data-testid="my-events-browse"
              className="myEventsBrowseLink"
            >
              <span className="btn-gold">
                Browse upcoming events <ArrowRight size={16} />
              </span>
            </Link>
          )}
        </div>
      </section>

      {rows.length > 0 && (
        <section className="section">
          <div className="container-x myEventsGrid">
            {rows.map((r) => (
              <MyEventCard key={r.registration_id} r={r} />
            ))}
          </div>
        </section>
      )}

      <PrivacySection />
    </div>
  );
}

/* --- unchanged components below, except MyEventCard formatting (timezone label) --- */

function PrivacySection() {
  const { user } = useAuth();
  const initial = !!(user && user.show_face_at_tables);
  const [enabled, setEnabled] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEnabled(!!(user && user.show_face_at_tables));
  }, [user]);

  const toggle = async () => {
    const next = !enabled;
    setSaving(true);
    setEnabled(next);
    try {
      toast.success(
        next
          ? "Your photo will now show on live-table previews."
          : "Your photo is hidden — visitors see your initials.",
      );
    } catch {
      setEnabled(!next);
      toast.error("Couldn't save privacy preference.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="section" data-testid="privacy-section">
      <div className="container-x privacySectionInner">
        <div className="glass privacyCard">
          <p className="overline privacyOverline">
            <EyeOff size={12} /> Privacy
          </p>
          <h2 className="privacyHeading">Your face on live tables</h2>
          <p className="privacyBody">
            By default, your profile photo is{" "}
            <strong className="privacyBodyStrong">hidden</strong>.
          </p>
          <button
            type="button"
            onClick={toggle}
            disabled={saving}
            data-testid="privacy-show-face-toggle"
            aria-pressed={enabled}
            className={`privacyToggleBtn ${enabled ? "privacyToggleBtnEnabled" : "privacyToggleBtnDisabled"}`}
          >
            <span className="privacyToggleBtnLeft">
              {enabled ? <Eye size={16} /> : <EyeOff size={16} />}
              <span className="privacyToggleBtnText">
                {enabled
                  ? "Show my photo on live tables"
                  : "Hide my photo from live tables"}
              </span>
            </span>
            <span
              className={`privacyToggleTrack ${enabled ? "privacyToggleTrackOn" : "privacyToggleTrackOff"}`}
            >
              <span
                className={`privacyToggleThumb ${enabled ? "privacyToggleThumbOn" : "privacyToggleThumbOff"}`}
              />
            </span>
          </button>
        </div>
      </div>
    </section>
  );
}

function MyEventCard({ r }) {
  const e = r.event;

  const now = Date.now();
  const startMs = e.start_time ? new Date(e.start_time).getTime() : NaN;
  const endMs = e.end_time ? new Date(e.end_time).getTime() : NaN;

  const isLive =
    Number.isFinite(startMs) &&
    Number.isFinite(endMs) &&
    now >= startMs &&
    now < endMs;
  const isPast = Number.isFinite(endMs) && now >= endMs;

  const dateLabel = e.displayDate || "TBA";
  const timeLabel = e.displayTimeRange
    ? `${e.displayTimeRange} ${e.timezone || ""}`
    : e.timezone || "";

  return (
    <article
      data-testid={`my-event-card-${r.registration_id}`}
      className="glass myEventCard"
    >
      <div className="myEventCardHeader">
        <p className="myEventCardDate">
          <Calendar size={11} /> {dateLabel}
        </p>
        {isLive && (
          <span className="myEventCardLiveBadge">
            <span className="myEventCardLiveDot" /> Live now
          </span>
        )}
        {isPast && <span className="myEventCardPastBadge">Past</span>}
      </div>

      <Link to={`/events/${e.id}`} className="myEventCardTitleLink">
        <h3 className="myEventCardTitle">{e.title}</h3>
      </Link>

      <p className="myEventCardTime">
        <Globe2 size={11} /> {timeLabel}
      </p>

      <div className="myEventCardBadges">
        <PaymentBadge
          status={r.payment_status}
          amount={r.price_paid}
          currency={r.currency}
        />

        {r.ref_code && (
          <span
            data-testid={`my-event-ref-${r.registration_id}`}
            className="myEventCardRefCode"
          >
            {r.ref_code}
          </span>
        )}

        {r.sponsor_attribution && (
          <span
            data-testid={`my-event-sponsor-${r.registration_id}`}
            className="myEventCardSponsor"
          >
            <Ticket size={10} /> Sponsored by {r.sponsor_attribution}
          </span>
        )}
      </div>

      <div className="myEventCardFooter">
        {isLive ? (
          <Link
            to={`/events/${e.id}`}
            data-testid={`my-event-join-${r.registration_id}`}
            className="myEventCardJoinBtn"
          >
            Enter event →
          </Link>
        ) : (
          !isPast && (
            <Link
              to={`/events/${e.id}`}
              data-testid={`my-event-detail-${r.registration_id}`}
              className="myEventCardAgendaLink"
            >
              See agenda <ArrowRight size={11} />
            </Link>
          )
        )}

        <a
          href={`${BACKEND}/api/events/${e.id}.ics`}
          download={`vmeetio-${e.id}.ics`}
          data-testid={`my-event-ics-${r.registration_id}`}
          className="myEventCardIcsLink"
        >
          <Calendar size={11} /> Add to calendar
        </a>
      </div>
    </article>
  );
}

function PaymentBadge({ status, amount, currency }) {
  if (status === "free")
    return (
      <span data-testid="payment-status-free" className="paymentBadgeGreen">
        <CheckCircle2 size={10} /> Free
      </span>
    );
  if (status === "voucher")
    return (
      <span data-testid="payment-status-voucher" className="paymentBadgeGreen">
        <CheckCircle2 size={10} /> Paid via voucher
      </span>
    );
  if (status === "paid")
    return (
      <span data-testid="payment-status-paid" className="paymentBadgeGreen">
        <CheckCircle2 size={10} /> Paid {currency} {amount}
      </span>
    );
  return (
    <span data-testid="payment-status-pending" className="paymentBadgeGold">
      <AlertCircle size={10} /> Payment pending · {currency} {amount}
    </span>
  );
}
